import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { connectObservation } from "./realtime-observation";
import type { RequestHistoryTargets } from "@nexra/contracts";
import type { RequestHistoryConnectionStatus } from "./request-history-stream";

import { clearPrivateQueries, privateQueryKey } from "./query-policy";

const AccountContext = createContext<string | undefined>(undefined);
const ObservationContext = createContext<{ status: RequestHistoryConnectionStatus; newRecordsAvailable: boolean; refreshHistory: () => void; watch: (targets: RequestHistoryTargets, key: string, frontPage: boolean) => void }>({ status: "unavailable", newRecordsAvailable: false, refreshHistory: () => {}, watch: () => {} });

export function AccountQueryProvider({ userId, children }: { userId: string | undefined; children?: ReactNode }) {
  const client = useQueryClient();
  const [status, setStatus] = useState<RequestHistoryConnectionStatus>("connecting");
  const [newRecordsAvailable, setNewRecordsAvailable] = useState(false);
  const connection = useRef<ReturnType<typeof connectObservation> | undefined>(undefined);
  useEffect(() => { void clearPrivateQueries(client, userId); }, [client, userId]);
  useEffect(() => {
    if (!userId) return;
    const stream = connectObservation(client, userId, setStatus, () => setNewRecordsAvailable(true));
    connection.current = stream;
    return () => { stream.close(); if (connection.current === stream) connection.current = undefined; };
  }, [client, userId]);
  const watch = useCallback((targets: RequestHistoryTargets, key: string, frontPage: boolean) => { connection.current?.watchHistory(targets, key, frontPage); }, []);
  const refreshHistory = useCallback(() => { setNewRecordsAvailable(false); connection.current?.refreshHistory(); }, []);
  return <AccountContext.Provider value={userId}><ObservationContext.Provider value={{ status, newRecordsAvailable, watch, refreshHistory }}>
    {children}
  </ObservationContext.Provider></AccountContext.Provider>;
}

export function useObservation() { return useContext(ObservationContext); }

export function useAccountQueryKey() {
  const userId = useContext(AccountContext);
  return useCallback((family: string, ...params: readonly unknown[]) => privateQueryKey(family, userId, ...params), [userId]);
}
